import { useEffect, useState, type ReactNode } from 'react';
import type { AppSession, InviteView } from '../lib/types';
import { ApiError, api } from '../lib/api';
import { firstName } from '../lib/session';
import { HeartMark } from '../components/HeartMark';

function Frame({ children, aside }: { children: ReactNode; aside: ReactNode }) {
  return (
    <div className="landing">
      <header className="landing-bar">
        <div className="landing-bar-inner">
          <a className="brand" href="#/signin">
            <HeartMark size={22} />
            <span>HeartCoach</span>
          </a>
          <span className="hint light small">Cardiac coaching, every day</span>
        </div>
      </header>
      <main className="landing-main">
        <div className="landing-copy">{aside}</div>
        <div className="auth-card">{children}</div>
      </main>
      <footer className="landing-foot">
        <div className="landing-foot-inner">
          <span className="hint small">HeartCoach supports your care. It never replaces your cardiologist. In an emergency, call 911.</span>
        </div>
      </footer>
    </div>
  );
}

/**
 * A clinic's invitation. The patient confirms the email address the invitation was sent to; only
 * that address, the one on the clinical manager's list, can create this account.
 */
export function InvitePage({ token, session, onLogin }: { token: string; session: AppSession | null; onLogin: (s: AppSession) => void }) {
  const [info, setInfo] = useState<InviteView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .inviteInfo(token)
      .then((v) => alive && setInfo(v))
      .catch((e: unknown) => alive && setLoadError(e instanceof ApiError ? e.message : 'Could not open this invitation.'));
    return () => {
      alive = false;
    };
  }, [token]);

  if (loadError) {
    return (
      <Frame aside={<h1>This invitation can't be used</h1>}>
        <h2>Invitation</h2>
        <p className="form-error">{loadError}</p>
        <a className="button secondary full" href="#/signin">
          Go to sign in
        </a>
      </Frame>
    );
  }
  if (!info) {
    return (
      <Frame aside={<h1>Opening your invitation…</h1>}>
        <p className="hint">One moment.</p>
      </Frame>
    );
  }

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.acceptInvite(token, email.trim());
      onLogin({ token: r.token, personId: r.personId, name: r.name, via: 'email' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create your account.');
      setBusy(false);
    }
  };

  return (
    <Frame
      aside={
        <>
          <span className="eyebrow">You're invited</span>
          <h1>
            {info.practiceName} prescribed <em>HeartCoach</em> for you.
          </h1>
          <p className="lede">
            {info.doctorName} wants to keep closer track of your heart between visits: a short daily check-in, reminders for your medications, your readings in one place, and a direct line to
            your care team.
          </p>
        </>
      }
    >
      <h2>Create your account</h2>
      <p className="hint">
        Hi {firstName(info.patientName)}. Confirm the email address this invitation was sent to (<span className="mono">{info.emailHint}</span>). Only that address can create this account.
      </p>
      {session ? (
        <p className="notice">
          You're signed in as {session.name} on this browser. Creating this account signs you in as {info.patientName} instead.
        </p>
      ) : null}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) void accept();
        }}
      >
        <label className="field">
          <span>Your email address</span>
          <input type="email" value={email} autoComplete="email" maxLength={254} placeholder="you@example.com" onChange={(e) => setEmail(e.target.value)} disabled={busy} />
        </label>
        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}
        <button className="primary big full" type="submit" disabled={busy || !email.trim()}>
          {busy ? 'Creating your account…' : 'Create my account'}
        </button>
      </form>
      <p className="hint small center" style={{ marginTop: '0.75rem' }}>
        Already created it? <a href="#/signin">Sign in with your email</a>.
      </p>
    </Frame>
  );
}

/** Tokens already spent in this page's lifetime, so a re-run effect never spends one twice. */
const spent = new Set<string>();

/** The landing spot of an emailed sign-in link: spend the token, then carry on signed in. */
export function EmailLinkPage({ token, onLogin }: { token: string; onLogin: (s: AppSession) => void }) {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (spent.has(token)) return;
    spent.add(token);
    api
      .emailComplete(token)
      .then((r) => onLogin({ token: r.token, personId: r.personId, name: r.name, via: 'email' }))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not sign you in.'));
  }, [token, onLogin]);
  return (
    <Frame aside={<h1>{error ? 'That link did not work' : 'Signing you in…'}</h1>}>
      <h2>Sign in</h2>
      {error ? (
        <>
          <p className="form-error">{error}</p>
          <a className="button secondary full" href="#/signin">
            Ask for a new link
          </a>
        </>
      ) : (
        <p className="hint">Checking your link…</p>
      )}
    </Frame>
  );
}
